import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { websocketService } from '../services/websocketService';
import { AppThunkDispatch } from '../store';
import { entitiesLoaded, loadEntities, updateCommunicationStatus } from '../store/entities/actions';
import { incomingMessageReceived, communicationMarkedAsRead, unreadMessagesMarkedAsRead, loadNotifications, tasksUpdated } from '../store/general/actions';
import { getAppointmentAsync, getPackAsync } from '../ajax/requests';

export const useWebSocket = (isLoggedIn: boolean) => {
    const dispatch = useDispatch<AppThunkDispatch>();
    const [connectionState, setConnectionState] = useState('Disconnected');
    const [error, setError] = useState<string | null>(null);

    const updateConnectionState = useCallback(() => {
        setConnectionState(websocketService.getConnectionState());
    }, []);

    useEffect(() => {
        if (!isLoggedIn) {
            websocketService.stop();
            updateConnectionState();
            return;
        }

        // Set up event handlers for WebSocket messages (only once)
        websocketService.onAppointmentCreated((appointment) => {
            console.log('Processing AppointmentCreated event:', appointment);
            dispatch(entitiesLoaded('appointments', [appointment], [], 'AppendResult'));
        });

        websocketService.onAppointmentUpdated((appointment) => {
            console.log('Processing AppointmentUpdated event:', appointment);
            if (typeof appointment === 'string') {
                dispatch(loadEntities('appointments', () => getAppointmentAsync(appointment).then(a => ([a])), 'AppendResult'))
            } else {
                dispatch(entitiesLoaded('appointments', [appointment], [appointment.id], 'AppendResult'));
            }
        });

        websocketService.onAppointmentDeleted((appointmentId) => {
            console.log('Processing AppointmentDeleted event:', appointmentId);
            dispatch(entitiesLoaded('appointments', [], [appointmentId], 'AppendResult'));
        });

        websocketService.onClientCreated((client) => {
            console.log('Processing ClientCreated event:', client);
            dispatch(entitiesLoaded('clients', [client], [], 'AppendResult'));
        });

        websocketService.onClientUpdated((client) => {
            console.log('Processing ClientUpdated event:', client);
            dispatch(entitiesLoaded('clients', [client], [client.id], 'AppendResult'));
        });

        websocketService.onClientDeleted((clientId) => {
            console.log('Processing ClientDeleted event:', clientId);
            dispatch(entitiesLoaded('clients', [], [clientId], 'AppendResult'));
        });

        websocketService.onProfessionalCreated((professional) => {
            console.log('Processing ProfessionalCreated event:', professional);
            dispatch(entitiesLoaded('professionals', [professional], [], 'AppendResult'));
        });

        websocketService.onProfessionalUpdated((professional) => {
            console.log('Processing ProfessionalUpdated event:', professional);
            dispatch(entitiesLoaded('professionals', [professional], [professional.id], 'AppendResult'));
        });

        websocketService.onProfessionalDeleted((professionalId) => {
            console.log('Processing ProfessionalDeleted event:', professionalId);
            dispatch(entitiesLoaded('professionals', [], [professionalId], 'AppendResult'));
        });

        websocketService.onIncomingCommunicationReceived((notification) => {
            console.log('Processing IncomingCommunicationReceived event:', notification);
            dispatch(incomingMessageReceived(notification));
        });

        websocketService.onCommunicationMarkedAsRead((communicationId, read) => {
            console.log('Processing CommunicationMarkedAsRead event:', communicationId);
            dispatch(communicationMarkedAsRead(communicationId, read));
        });

        websocketService.onAllCommunicationsMarkedAsRead(() => {
            console.log('Processing AllCommunicationsMarkedAsRead event');
            dispatch(unreadMessagesMarkedAsRead());
        });

        websocketService.onCommunicationUpdated((communication) => {
            console.log('Processing CommunicationUpdated event:', communication.id);
            dispatch(entitiesLoaded('communications', [communication], [], 'AppendResult'));
        });

        websocketService.onCommunicationStatusUpdated((update) => {
            console.log('Processing CommunicationStatusUpdated event:', update);
            dispatch(updateCommunicationStatus(
                update.id,
                update.status,
                update.statusUpdateTimestamp,
            ));
        });

        websocketService.onPackUpdated((pack) => {
            console.log('Processing PackUdpated event:', pack);
            
            if (typeof pack === 'string') {
                dispatch(loadEntities('clientPacks', () => getPackAsync(pack).then(a => ([a])), 'AppendResult'))
            } else {
                dispatch(entitiesLoaded('clientPacks', [pack], [pack.id], 'AppendResult'));
            }
        })

        websocketService.onNotificationsUpdated(() => {
            dispatch(loadNotifications());
        });

        websocketService.onTasksUpdated(() => {
            dispatch(tasksUpdated());
        });

        // Start the WebSocket connection
        const startConnection = async () => {
            try {
                setError(null);
                await websocketService.start();
                updateConnectionState();
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to connect to WebSocket';
                console.error('Failed to start WebSocket connection:', errorMessage);
                setError(errorMessage);
                updateConnectionState();
            }
        };

        startConnection();

        // Update connection state periodically
        const stateInterval = setInterval(updateConnectionState, 1000);

        // Cleanup function
        return () => {
            clearInterval(stateInterval);
        };
    }, [isLoggedIn, dispatch, updateConnectionState]);

    return {
        connectionState,
        isConnected: websocketService.isConnected(),
        error,
    };
};
